import React, { useEffect } from 'react'
import { useFingerprint } from '../../../hooks/useFingerPrint'
import { createUuid } from '../../../Utils/string'
import { FRONT_UUID_STORAGE_KEY, useLocalStorage } from '../../../Utils/useLocalStorage'
import { LikeButton } from './LikeButton'
import { useLike } from './useLike'

interface LikeFirmButtonProps {
  firmId: number
  className?: string
}

/**
 * A button component that allows users to like/unlike a firm
 * @param props - The props for the component
 * @param props.firmId - The ID of the firm to like/unlike
 * @param props.frontUuid - The UUID of the user performing the action
 * @param props.initialLikesCount - Optional initial count of likes
 * @param props.initialIsLiked - Optional initial liked state
 * @param props.className - Optional additional CSS classes
 */
export const LikeFirmButton: React.FC<LikeFirmButtonProps> = ({
  firmId,
  className,
}) => {
  const [frontUuid, setFrontUuid] = useLocalStorage<string>(FRONT_UUID_STORAGE_KEY, '')

  useEffect(() => {
    if (!frontUuid) {
      setFrontUuid(createUuid())
    }
  }, [frontUuid, setFrontUuid])

  const {
    fingerprint,
    error: fingerprintError,
    isLoading: isFingerprintLoading,
  } = useFingerprint()

  const {
    isLiked,
    likesCount,
    isLoading: isLikeLoading,
    toggleLike,
  } = useLike({
    firmId,
    frontUuid,
    fingerprint,
    initialLikesCount: 0,
    initialIsLiked: false,
  })

  // If we're still loading the visitor ID or there was an error, show a disabled button
  if (isFingerprintLoading || fingerprintError) {
    if (!fingerprint) {
      return null
    }
    return (
      <div className={className} data-testid="like-button-loading">
        <LikeButton
          isLiked={false}
          likesCount={0}
          isLoading
          onClick={() => {
            console.warn('fingerprintError', fingerprintError)
          }}
        />
      </div>
    )
  }

  if (!fingerprint) {
    return null
  }

  return (
    <div className={className} data-testid="like-button-loaded">
      <LikeButton
        isLiked={isLiked}
        likesCount={likesCount}
        isLoading={isLikeLoading}
        onClick={toggleLike}
      />
    </div>
  )
} 
